/**
 * Where a document sits on the desk (desk pass, 2026-09-03).
 *
 * The old desk was a ladder: a card's spot and tilt came from its index among
 * the cards currently on the desk, so opening one card moved every card after
 * it, the ninth card sat exactly on the first, and a long session scrolled
 * cards out of sight. Now a document has a PLACE — a pair of fractions of the
 * desk's free range — decided once and never by anything else on the desk.
 *
 * Fractions rather than rem, deliberately. A place of (0.3, 0.6) is the same
 * spot on a 1280 laptop and a 2560 monitor: the desk stretches and the cards
 * stretch with it, nothing needs clamping back, and a card can never be
 * further out than the desk's own edge because the range is [0, 1] by
 * construction. That is the whole reachability guarantee.
 *
 * Pure: no DOM, no clock, no Math.random. The repo bans Math.random outright
 * (CLAUDE.md conventions) and this is presentation, not play state, so the
 * seed is derived from two ids that already exist and persist — the session
 * and the document. The landing is a pure function of (session, delivery
 * order), so a cleared localStorage re-lands the desk identically.
 */

/** Where a card sits: fractions of the desk's free range, both in [0, 1]. */
export interface DeskPlace {
  readonly fx: number;
  readonly fy: number;
}

/** A rem offset from the stack's top-left — the place resolved against real bounds. */
export interface DeskSpot {
  readonly x: number;
  readonly y: number;
}

/** The stack container's inner size, in rem. */
export interface DeskBounds {
  readonly w: number;
  readonly h: number;
}

export const CARD_W_REM = 9.25;
/** 266px at 16px root: the 40%-taller card of 2026-09-22 (was 190px, 11.9rem). */
export const CARD_H_REM = 16.6;

/**
 * A card's size in rem. The Storyteller desk is short and its scoped tokens
 * shrink the paper as the panel shrinks, so the free range a place resolves
 * against has to be told how big the paper actually is right now. Omitted, the
 * design's own card size is used, which is what every existing call meant.
 */
export interface CardSize {
  readonly w: number;
  readonly h: number;
}

const DESIGN_CARD: CardSize = { w: CARD_W_REM, h: CARD_H_REM };

/**
 * The nominal desk: roughly the 1920x1080 column less its margins and the
 * notes control. Used when the stack cannot be measured (first paint, jsdom)
 * and as the yardstick for how far apart two landings are.
 */
export const FALLBACK_BOUNDS: DeskBounds = { w: 29, h: 38 };

/** Tilt range in degrees; frame 11's cards sit between about -1 and +1.1. */
const TILT_MAX_DEG = 1.2;

/** Best-candidate sampling: more candidates spread cards further apart at the cost of looking less random. */
const LANDING_CANDIDATES = 12;

function fnv1a(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** mulberry32 — small, seedable, and good enough for where a card sits. */
function prng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFor(sessionId: string, docId: string, stream: string): number {
  return fnv1a(`${sessionId}:${docId}:${stream}`);
}

function unit(value: number): number {
  return Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), 1);
}

function round(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/** The furthest a card's top-left corner may sit and still be wholly inside the bounds. */
export function maxSpot(bounds: DeskBounds, card: CardSize = DESIGN_CARD): DeskSpot {
  return { x: Math.max(bounds.w - card.w, 0), y: Math.max(bounds.h - card.h, 0) };
}

/** Every place, wherever it came from, is confined to the desk. */
export function clampPlace(place: DeskPlace): DeskPlace {
  return { fx: unit(place.fx), fy: unit(place.fy) };
}

/** Resolve a place against the desk as it is right now. */
export function toSpot(place: DeskPlace, bounds: DeskBounds, card: CardSize = DESIGN_CARD): DeskSpot {
  const max = maxSpot(bounds, card);
  const p = clampPlace(place);
  return { x: round(p.fx * max.x), y: round(p.fy * max.y) };
}

/** A rem offset (a drop point) back into a place. A desk too small to move on collapses to the origin. */
export function toPlace(spot: DeskSpot, bounds: DeskBounds, card: CardSize = DESIGN_CARD): DeskPlace {
  const max = maxSpot(bounds, card);
  return { fx: round(unit(max.x > 0 ? spot.x / max.x : 0)), fy: round(unit(max.y > 0 ? spot.y / max.y : 0)) };
}

/** A card's permanent tilt in degrees. Independent of position and of every other card. */
export function tiltFor(sessionId: string, docId: string): number {
  const next = prng(seedFor(sessionId, docId, "tilt"));
  return Math.round((next() * 2 - 1) * TILT_MAX_DEG * 100) / 100;
}

function clearance(a: DeskPlace, b: DeskPlace): number {
  const max = maxSpot(FALLBACK_BOUNDS);
  return Math.hypot((a.fx - b.fx) * max.x, (a.fy - b.fy) * max.y);
}

/**
 * Where each document landed, in delivery order.
 *
 * Each document draws a handful of candidates from its own seeded stream and
 * keeps the one furthest from the nearest EARLIER landing (Mitchell's
 * best-candidate). It still reads as a scatter, but a new card is far less
 * likely to land squarely on an old one — the failure the ladder had by
 * construction at nine cards.
 *
 * Earlier LANDINGS, not earlier cards' current spots: where the learner has
 * since dragged things does not enter into it. That keeps the result a pure
 * function of the delivery order, so the desk re-lands identically after a
 * cleared localStorage, and a document's place is never changed by anything
 * the learner did to a different document.
 */
export function landingPlaces(sessionId: string, docIds: readonly string[]): ReadonlyMap<string, DeskPlace> {
  const places = new Map<string, DeskPlace>();
  const landed: DeskPlace[] = [];
  for (const docId of docIds) {
    if (places.has(docId)) continue;
    const next = prng(seedFor(sessionId, docId, "land"));
    let best: DeskPlace = { fx: 0, fy: 0 };
    let bestClearance = -1;
    for (let i = 0; i < LANDING_CANDIDATES; i += 1) {
      const candidate: DeskPlace = { fx: round(next()), fy: round(next()) };
      const nearest = landed.reduce((min, other) => Math.min(min, clearance(candidate, other)), Number.POSITIVE_INFINITY);
      if (nearest > bestClearance) {
        best = candidate;
        bestClearance = nearest;
      }
    }
    places.set(docId, best);
    landed.push(best);
  }
  return places;
}
